import React, { useEffect, useState } from 'react';
import { getMobilePayments, getMobilePaymentStats, getPartenaires, syncMobilePayments } from '../services/api';

const MobilePayments: React.FC = () => {
  const [payments, setPayments] = useState<any[]>([]);
  const [partenaires, setPartenaires] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<any>({});
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  useEffect(() => {
    getPartenaires().then(r => setPartenaires(r.data)).catch(() => {});
  }, []);

  // Build query params from filters
  const buildParams = (extra?: Record<string, string>) => {
    const params: Record<string, string> = { ...extra };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v as string; });
    return params;
  };

  // Reload stats when filters change
  useEffect(() => {
    getMobilePaymentStats(buildParams()).then(r => setStats(r.data)).catch(() => {});
  }, [filters]);

  // Reload list when filters or page change
  useEffect(() => {
    getMobilePayments(buildParams({ page: page.toString(), limit: '30' })).then(r => {
      setPayments(r.data.data || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.totalPages || 1);
    }).catch(() => {});
  }, [page, filters]);

  const updateFilter = (key: string, value: string) => {
    setFilters((prev: any) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const reloadData = () => {
    getMobilePaymentStats(buildParams()).then(r => setStats(r.data)).catch(() => {});
    getMobilePayments(buildParams({ page: page.toString(), limit: '30' })).then(r => {
      setPayments(r.data.data || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.totalPages || 1);
    }).catch(() => {});
  };

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncMessage(null);
    try {
      const r = await syncMobilePayments();
      const d = r.data;
      setSyncMessage(
        d.lignes_inserees > 0
          ? `${d.lignes_inserees} paiement(s) synchronise(s). Total lie : ${d.total_lies} / ${d.total_paiements_recus}.`
          : `Deja a jour. ${d.total_lies} / ${d.total_paiements_recus} paiements synchronises.`
      );
      reloadData();
    } catch (e: any) {
      setSyncMessage('Erreur : ' + (e?.response?.data?.error || e?.message || 'inconnue'));
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMessage(null), 8000);
    }
  };

  const formatMontant = (v: number) => {
    return Number(v || 0).toLocaleString('fr-FR');
  };

  const getMethodeBadge = (type: string) => {
    const isWave = type?.toLowerCase() === 'wave';
    return (
      <span style={{
        padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
        background: isWave ? '#e3f2fd' : '#fff3e0',
        color: isWave ? '#1565c0' : '#e65100'
      }}>
        {isWave ? 'Wave' : 'Orange Money'}
      </span>
    );
  };

  const moisNoms = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ color: '#333', margin: 0 }}>Paiements Mobile Money ({total.toLocaleString('fr-FR')})</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {syncMessage && (
            <span style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500,
              background: syncMessage.startsWith('Erreur') ? '#ffebee' : '#e8f5e9',
              color: syncMessage.startsWith('Erreur') ? '#c62828' : '#2e7d32'
            }}>{syncMessage}</span>
          )}
          <button
            onClick={reloadData}
            disabled={syncing}
            title="Recharger les donnees"
            style={{
              padding: '8px 14px', borderRadius: 8, border: '1px solid #1976d2',
              background: '#fff', color: '#1976d2', cursor: syncing ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            <span>&#8635;</span> Actualiser
          </button>
          <button
            onClick={handleSync}
            disabled={syncing}
            title="Recalcule et aligne les tables paiements_recus et paiements_recus_factures"
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none',
              background: syncing ? '#bdbdbd' : '#1976d2', color: '#fff',
              cursor: syncing ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            <span style={{ display: 'inline-block', animation: syncing ? 'spin 1s linear infinite' : 'none' }}>&#9851;</span>
            {syncing ? 'Synchronisation...' : 'Synchroniser les donnees'}
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="dashboard-cards">
        <div className="dash-card bg-blue">
          <span className="dash-card-icon">&#128179;</span>
          <h4>Total Paiements</h4>
          <div className="dash-card-value">{stats?.totalCount?.toLocaleString('fr-FR') || 0}</div>
        </div>
        <div className="dash-card bg-teal">
          <span className="dash-card-icon">&#127754;</span>
          <h4>Wave ({stats?.waveCount?.toLocaleString('fr-FR') || 0})</h4>
          <div className="dash-card-value" style={{ fontSize: 20, whiteSpace: 'nowrap' }}>{formatMontant(stats?.waveTotal || 0)} FCFA</div>
        </div>
        <div className="dash-card bg-orange">
          <span className="dash-card-icon">&#128241;</span>
          <h4>Orange Money ({stats?.omCount?.toLocaleString('fr-FR') || 0})</h4>
          <div className="dash-card-value" style={{ fontSize: 20, whiteSpace: 'nowrap' }}>{formatMontant(stats?.omTotal || 0)} FCFA</div>
        </div>
        <div className="dash-card bg-green">
          <span className="dash-card-icon">&#128176;</span>
          <h4>Montant Total</h4>
          <div className="dash-card-value" style={{ fontSize: 20, whiteSpace: 'nowrap' }}>{formatMontant(stats?.totalMontant || 0)} FCFA</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <select value={filters.type_paiement || ''} onChange={e => updateFilter('type_paiement', e.target.value)}>
          <option value="">Toutes les methodes</option>
          <option value="wave">Wave</option>
          <option value="om">Orange Money</option>
        </select>
        <select value={filters.partenaire_id || ''} onChange={e => updateFilter('partenaire_id', e.target.value)}>
          <option value="">Tous les partenaires</option>
          {partenaires.map(p => <option key={p.id} value={p.id}>{p.libelle}</option>)}
        </select>
        <select value={filters.mois || ''} onChange={e => updateFilter('mois', e.target.value)}>
          <option value="">Tous les mois</option>
          {moisNoms.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <select value={filters.annee || ''} onChange={e => updateFilter('annee', e.target.value)}>
          <option value="">Toutes les annees</option>
          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <input
          type="date"
          value={filters.date_debut || ''}
          onChange={e => updateFilter('date_debut', e.target.value)}
          title="Date debut"
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
        />
        <input
          type="date"
          value={filters.date_fin || ''}
          onChange={e => updateFilter('date_fin', e.target.value)}
          title="Date fin"
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
        />
        {Object.values(filters).some(v => v) && (
          <button
            onClick={() => { setFilters({}); setPage(1); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#ef5350', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            Reinitialiser
          </button>
        )}
      </div>

      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date paiement</th>
              <th>Ref. Facture</th>
              <th>Ref. Abonne</th>
              <th>Abonne</th>
              <th>Partenaire</th>
              <th>Periode</th>
              <th>Methode</th>
              <th>Montant (FCFA)</th>
              <th>Numero</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p: any) => (
              <tr key={p.id}>
                <td>{p.date_paiement ? new Date(p.date_paiement).toLocaleDateString('fr-FR') : '-'}</td>
                <td><strong>{p.reference || '-'}</strong></td>
                <td>{p.ref_abonne || '-'}</td>
                <td>{p.abonne?.trim() || '-'}</td>
                <td>{p.partenaire || '-'}</td>
                <td>{p.facture_mois && p.facture_annee ? `${moisNoms[p.facture_mois]} ${p.facture_annee}` : '-'}</td>
                <td>{getMethodeBadge(p.type_paiement)}</td>
                <td>{p.montant?.toLocaleString('fr-FR')} FCFA</td>
                <td>{p.numero || '-'}</td>
              </tr>
            ))}
            {payments.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucun paiement mobile trouve</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Precedent</button>
        <span style={{ padding: '8px 14px', fontSize: 14 }}>Page {page} / {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Suivant</button>
      </div>
    </div>
  );
};

export default MobilePayments;
